import { Component, OnInit } from '@angular/core';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    NavigationEnd,
    Router,
} from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LookupService } from '../demo/service/lookup.service';
import { WebStorage } from '../demo/components/auth/storage/web.storage';
import { Common } from '../demo/utils/common';

interface Breadcrumb {
    label: string;
    url?: string;
}

@Component({
    selector: 'app-breadcrumb',
    templateUrl: './app.breadcrumb.component.html',
})
export class AppBreadcrumbComponent implements OnInit {
    private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);

    readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private lookupService: LookupService,
        private storage: WebStorage,
    ) { }

    ngOnInit() {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event) => {
                this.setupBreadcrumbs();
            });

        this.activatedRoute.url.subscribe(() => {
            this.setupBreadcrumbs();
        });
    }
handleBreadcrumbClick(event: MouseEvent, item: any) {
    // If no URL is set (no permission), prevent navigation
    if (!item.url) {
        event.preventDefault();
        event.stopPropagation();
        return false;
    }

    // Check permissions before allowing navigation
    if (!this.hasPermissionForUrl(item.url)) {
        event.preventDefault();
        event.stopPropagation();
        return false;
    }

    // Allow routerLink to handle navigation naturally
    // Don't prevent default or navigate manually - let Angular handle it
    return true;
}

getRouterLink(url: string): string[] {
    if (!url) return [];
    // Ensure URL starts with /
    const normalizedUrl = url.startsWith('/') ? url : '/' + url;
    // Convert URL string to router link array
    const segments = normalizedUrl.split('/').filter((segment: string) => segment.length > 0);
    return segments;
}

getRouterLinkActive(url: string): string[] {
    if (!url) return [];
    // For active route detection, return the full path
    return ['/' + url.split('/').filter((segment: string) => segment.length > 0).join('/')];
}

getQueryParams(url: string): any {
    // For now, return empty object - can be extended for query params if needed
    return {};
}

private checkOwnership(studentId: string): boolean {
    // Get current user data
    const userData = this.storage.getUserData();
    if (!userData) return false;

    // For parents/guardians, check if they have access to this student
    const userRole = ((userData?.role || userData?.refTable) || '').toString().trim().toLowerCase();
    const isParentOrGuardian = userRole === 'parent' || userRole === 'guardian';

    if (isParentOrGuardian) {
        // Check if the student ID matches the parent's associated student
        const activeStudent = localStorage.getItem('activeStudent');
        if (activeStudent) {
            try {
                const activeStudentData = JSON.parse(activeStudent);
                return activeStudentData.id.toString() === studentId;
            } catch (e) {
                console.error('Error parsing activeStudent:', e);
            }
        }
        return false;
    }

    // For staff/admin, allow access
    return true;
}

private hasPermissionForUrl(url: string): boolean {
// Admin has full access
if (Common.isAdminUserLogin(this.storage)) {
    return true;
}

// Check permissions based on URL patterns (same as PermissionGuard)
// Staff overview and list - Admin only
if (url === '/staff' || url.includes('/staff/overview') || url.includes('/staff-list')) {
    return Common.isAdminUserLogin(this.storage);
}

// Staff attendance view - Admin only
if (url.includes('/staff/viewAttendance/daily-view')) {
    return Common.isAdminUserLogin(this.storage);
}

// Other staff routes - require staff permissions
if (url.startsWith('/staff/')) {
    return this.storage.hasAnyAuthority([
        'Staff Profile (Full Access)',
        'Staff Profile (Read)',
        'Staff (Full Access)',
        'Staff (Read)'
    ]);
}

if (url.includes('/myschool/student') || url.includes('/student-Sign-In')) {
    return this.storage.hasAnyAuthority([
        'Student Profile (Full Access)',
        'Student Profile (Read)',
        'Student (Full Access)',
        'Student (Read)'
    ]);
}

if (url.includes('/myschool/room') || url.includes('/room')) {
    return this.storage.hasAnyAuthority([
        'Room Profile (Read)',
        'Room Profile (Full Access)'
    ]);
}

if (url.includes('/attendance') || url.includes('dailyAttendance') ||
    url.includes('viewAttendance')) {
    return this.storage.hasAnyAuthority([
        'Attendance Daily (Full Access)',
        'Attendance Daily (Read)',
        'Attendance Monthly (Read)',
        'Attendance Absent (Full Access)',
        'Attendance Absent (Read)',
        'Staff Attendance (Full Access)'
    ]);
}

if (url.includes('/parentConnection/activities') ||
    url.includes('/activities/all')) {
    return this.storage.hasAnyAuthority([
        'Parent Connection Newsletter (Full Access)',
        'Parent Connection Newsletter (Read)',
        'Parent Connection Daily Activity (Read)',
        'Parent Connection Daily Activity (Full Access)',
        'Parent Connection Daily Activity Feed (Read)',
        'Parent Connection Daily Activity Feed (Full Access)'
    ]);
}

if (url.includes('/parentConnection') || url.includes('/communication')) {
    return this.storage.hasAnyAuthority([
        'Parent Connection Newsletter (Full Access)',
        'Parent Connection Newsletter (Read)',
        'Parent Connection Message (Full Access)',
        'Prent Connection Message (Read)',
        'Parent Connection Daily Activity (Read)',
        'Parent Connection Daily Activity (Full Access)',
        'Parent Connection Daily Activity Feed (Full Access)'
    ]);
}

if (url.includes('/scheduling/classroom-view')) {
    // Classroom view requires full access (admin only)
    return this.storage.hasAnyAuthority([
        'Staff Scheduling (Full Access)'
    ]);
}

if (url.includes('/scheduling/staff-view')) {
    // Staff view can be accessed by staff with read or full access
    return this.storage.hasAnyAuthority([
        'Staff Scheduling (Full Access)',
        'Staff Scheduling (Read)'
    ]);
}

if (url.includes('/scheduling')) {
    // General scheduling access
    return this.storage.hasAnyAuthority([
        'Staff Scheduling (Full Access)',
        'Staff Scheduling (Read)'
    ]);
}

if (url.includes('/billing/accounting/family-transaction') ||
    url.includes('/billings/accounting/family-transaction') ||
    url.includes('/billing/accounting/familylayout/family-transaction') ||
    url.includes('/billings/accounting/familylayout/family-transaction')) {
    // Family transaction - allow parent access with ownership validation
    const hasBillingPermission = this.storage.hasAnyAuthority([
        'Billing Accounting (Full Access)',
        'Billing Accounting (Read)',
        'Billing Recurring Active Plans (Full Access)',
        'Billing Recurring Active Plans (Read)',
        'Billing Recurring Plan Templates (Full Access)',
        'Billing Recurring Plan Templates (Read)',
        'All Transaction List (Full Access)',
        'All Transaction List (Read)'
    ]);

    // For parents, also check ownership
    const userData = this.storage.getUserData();
    const userRole = ((userData?.role || userData?.refTable) || '').toString().trim().toLowerCase();
    const isParentOrGuardian = userRole === 'parent' || userRole === 'guardian';

    if (hasBillingPermission && isParentOrGuardian) {
        // Extract student ID from URL for ownership check
        let studentId = null;

        // Try to extract from route param (old way)
        const studentIdMatch = url.match(/family-transaction\/(\d+)/);
        if (studentIdMatch) {
            studentId = studentIdMatch[1];
        } else {
            // Try to extract from query param (new way)
            const urlObj = new URL(url, window.location.origin);
            studentId = urlObj.searchParams.get('id');
        }

        if (studentId) {
            // Check if parent has ownership of this student
            return this.checkOwnership(studentId);
        }
    }

    return hasBillingPermission;
}

if (url.includes('/billing')) {
    // All other billing routes - Admin only
    return this.storage.hasAnyAuthority([
        'Billing Accounting (Full Access)'
    ]);
}

if (url.includes('/reports')) {
    return this.storage.hasAnyAuthority([
        'Reports (Full Access)',
        'Reports (Read)'
    ]);
}

if (url.includes('/future-enrollment') || url.includes('/availability')) {
    return this.storage.hasAnyAuthority([
        'Room Move (Full Access)',
        'Enrollment (Full Access)',
        'Availability (Read)'
    ]) && Common.isAdminUserLogin(this.storage);
}

if (url.includes('/settings') || url.includes('/gen-settings')) {
    return this.storage.hasAnyAuthority([
        'School Settings General (Edit)',
        'School Settings Immunization (Full Access)',
        'School Settings Immunization (Read)',
        'School Settings Custom Fields (Full Access)',
        'School Settings Custom Fields (Read)',
        'Staff (Full Access)'
    ]);
}

// For routes that don't have specific permission requirements, allow access
return true;
}

    async setupBreadcrumbs() {
        const root: ActivatedRouteSnapshot = this.activatedRoute.snapshot.root;
        const breadcrumbs: Breadcrumb[] = [];
        await this.addBreadcrumb(root, [], breadcrumbs);
        // If current route is a staff attendance/profile path, remove the intermediate
        // 'Staff' breadcrumb so breadcrumbs read: Home > Staff Attendance > <Name>
        try {
            const currentUrl = (this.router && this.router.url) ? this.router.url : (window && window.location && window.location.hash) ? window.location.hash : '';
            const isStaffProfilePath = currentUrl && (currentUrl.indexOf('staffAttendanceProfile') !== -1 || currentUrl.indexOf('/staff/viewAttendance') !== -1 || currentUrl.indexOf('staff-attendance') !== -1 || currentUrl.indexOf('staffAttendance') !== -1);
            if (isStaffProfilePath) {
                const idx = breadcrumbs.findIndex(b => (b.label || '').toString().trim() === 'Staff');
                if (idx !== -1) {
                    breadcrumbs.splice(idx, 1);
                }
            }

            // If this is a student attendance route, remove the intermediate 'Attendance' breadcrumb
            const isStudentAttendancePath = currentUrl && (currentUrl.indexOf('student-Sign-In') !== -1 || currentUrl.indexOf('dailyAttendance') !== -1 || currentUrl.indexOf('student-attendance') !== -1 || currentUrl.indexOf('studentAttendance') !== -1);
            if (isStudentAttendancePath) {
                const idx2 = breadcrumbs.findIndex(b => (b.label || '').toString().trim() === 'Attendance');
                if (idx2 !== -1) {
                    breadcrumbs.splice(idx2, 1);
                }
            }

            // Special-case: when viewing a student's attendance profile view (profileViewAttendance)
            // we want: Home > Student Attendance > <Student Name>
            if (currentUrl && currentUrl.indexOf('profileViewAttendance') !== -1) {
                // ensure 'Student Attendance' breadcrumb exists right after Home
                const hasStudentAttendance = breadcrumbs.some(b => (b.label || '').toString().trim() === 'Student Attendance');
                if (!hasStudentAttendance) {
                    const studentAttendanceUrl = '/student-Sign-In/dailyAttendance';
                    const permitted = this.hasPermissionForUrl(studentAttendanceUrl);
                    const homeIndex = breadcrumbs.findIndex(b => (b.label || '').toString().trim() === 'Home');
                    const insertAt = homeIndex === -1 ? 0 : homeIndex + 1;
                    breadcrumbs.splice(insertAt, 0, { label: 'Student Attendance', url: permitted ? studentAttendanceUrl : undefined });
                }
            }
        } catch (e) {
            // swallow any errors reading router/window to avoid breaking breadcrumb setup
        }

        this._breadcrumbs$.next(breadcrumbs);
    }

 private async addBreadcrumb(
 route: ActivatedRouteSnapshot,
 parentUrl: string[],
 breadcrumbs: Breadcrumb[]
) {
 const routeUrl = parentUrl.concat(route.url.map((url) => url.path));
 const breadcrumb = route.data['breadcrumb'];
    const parentBreadcrumb =
        route.parent && route.parent.data
            ? route.parent.data['breadcrumb']
            : null;

    // Get user role once
    const userData = this.storage.getUserData();
    const userRole = ((userData?.role || userData?.refTable) || '').toString().trim().toLowerCase();
    const isParentOrGuardian = userRole === 'parent' || userRole === 'guardian';
    const isStaff = userRole === 'staff';
    const isAdmin = Common.isAdminUserLogin(this.storage);

    // Special handling for staff users in attendance/profile routes
    const isStaffAttendanceRoute = routeUrl.some(segment =>
        segment.includes('staffAttendanceProfile') ||
        segment.includes('viewAttendance') ||
        segment.includes('staff-attendance') ||
        segment.includes('staffAttendance') ||
        segment.includes('staff-attendance')
    );

    // Check if this breadcrumb is a staff-related parent breadcrumb
    const isStaffParentBreadcrumb = breadcrumb === 'Staff' || breadcrumb === 'Staff Attendance' ||
                                   breadcrumb === 'Staff Management' || breadcrumb === 'Staff Overview';

        if (breadcrumb && breadcrumb != parentBreadcrumb) {
            // If the breadcrumb is the top-level 'Students' and the user is a guardian/parent,
            // skip adding it so guardians don't see the 'Students' breadcrumb.
            if (breadcrumb === 'Students' && isParentOrGuardian) {
                // Continue traversal to add child breadcrumbs but don't push this one.
                if (route.firstChild) {
                    await this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
                }
                return;
            }
        // Handle Family Layout breadcrumb - convert to Family Account hierarchy
        if (breadcrumb == 'Family Layout') {
            const accountingUrl = '/billing/accounting/family';
            const hasAccountingPermission = this.hasPermissionForUrl(accountingUrl);

            // Show Family Account breadcrumb - clickable only for admin
            const accounting = {
                label: 'Family Account',
                url: isAdmin ? accountingUrl : undefined
            }
            breadcrumbs.push(accounting);

            // Get student ID from query params
            const currentUrl = this.router.url;
            const urlObj = new URL(currentUrl, window.location.origin);
            const studentId = urlObj.searchParams.get('id');

            // Validate access before displaying student information
            let hasAccess = false;
            let studentName = '';

            if (studentId) {
                // Check if user has access to this student
                if (isParentOrGuardian) {
                    // For parents, check ownership
                    hasAccess = this.checkOwnership(studentId);
                } else if (isAdmin) {
                    // For admins, check school access
                    try {
                        const req = await this.lookupService.getStudentById(+studentId).toPromise();
                        const student = req?.body;
                        const userData = this.storage.getUserData();
                        hasAccess = student && userData?.schoolId && student.schoolId === userData.schoolId;
                    } catch (error) {
                        console.error('Error validating school access for breadcrumb:', error);
                        hasAccess = false;
                    }
                }

                // Only fetch and display student name if user has access
                if (hasAccess) {
                    try {
                        const req = await this.lookupService.getStudentById(+studentId).toPromise();
                        let fullName = req?.body?.fullName ?? "";
                        let nameArray = fullName.split(" ");
                        studentName = nameArray.length > 1 ? nameArray[nameArray.length - 1] : '';
                    } catch (error) {
                        console.error('Error fetching student name for breadcrumb:', error);
                        studentName = '';
                    }
                }
            }

            // Determine which tab we're on based on URL
            let childLabel = '';
            if (currentUrl.includes('/family-transaction')) {
                childLabel = hasAccess && studentName ? `${studentName} Family Transactions` : 'Family Transactions';
            } else if (currentUrl.includes('/statement')) {
                childLabel = hasAccess && studentName ? `${studentName} Family Statements` : 'Family Statements';
            }

            if (childLabel) {
                const breadcrumbUrl = '/' + routeUrl.join('/');
                const hasPermission = false; // Only allow navigation if user has access

                breadcrumbs.push({
                    label: childLabel,
                    url: hasPermission ? breadcrumbUrl : undefined,
                });
            }

        } else if (breadcrumb == 'Student Activity Profile' || breadcrumb == 'Classroom' || breadcrumb == 'Shift Profile' || breadcrumb == 'Schedule Profile' || breadcrumb == 'Overview') {
            const label = `${route.data['breadcrumb']} ${route.params['id'] ? ' / ' + (await this.getName(breadcrumb, route.params['id'])) : ''}`;
            const breadcrumbUrl = '/' + routeUrl.join('/');
            const hasPermission = this.hasPermissionForUrl(breadcrumbUrl);

            // For staff users in attendance/profile routes, disable parent breadcrumbs
            let allowNavigation = isAdmin || (isStaff && hasPermission) || (isParentOrGuardian && hasPermission);

            if (isStaff && isStaffAttendanceRoute && isStaffParentBreadcrumb) {
                // Staff users cannot navigate to staff parent breadcrumbs in attendance routes
                allowNavigation = false;
            }

            breadcrumbs.push({
                label: label,
                url: allowNavigation ? breadcrumbUrl : undefined,
            });
        } else if (breadcrumb == `Student's Attendance`) {
            // For Student's Attendance routes we want to show only the student name
            // without the 'Student's Attendance' prefix. Fetch the student name and
            // push it as the single breadcrumb label (e.g., 'Kevin Falon').
            if (route.params['id']) {
                const profileName = await this.getName(`Student's Attendance`, route.params['id']);
                const breadcrumbUrl = '/' + routeUrl.join('/');
                const hasPermission = this.hasPermissionForUrl(breadcrumbUrl);

                let allowNavigation = isAdmin || (isStaff && hasPermission) || (isParentOrGuardian && hasPermission);

                if (isStaff && isStaffAttendanceRoute && isStaffParentBreadcrumb) {
                    allowNavigation = false;
                }

                breadcrumbs.push({
                    label: profileName || route.data['breadcrumb'],
                    url: allowNavigation ? breadcrumbUrl : undefined,
                });
            }
        } else if (breadcrumb == 'Staff Attendance Profile') {
            // For Staff Attendance Profile routes we want to show only the staff name
            // without the 'Staff Attendance Profile' prefix. Fetch the staff name and
            // push it as the single breadcrumb label (e.g., 'John Doe').
            if (route.params['id']) {
                const profileName = await this.getName('Staff Attendance Profile', route.params['id']);
                const breadcrumbUrl = '/' + routeUrl.join('/');
                const hasPermission = this.hasPermissionForUrl(breadcrumbUrl);

                let allowNavigation = isAdmin || (isStaff && hasPermission) || (isParentOrGuardian && hasPermission);

                // Staff users in attendance routes should not navigate to parent breadcrumbs
                if (isStaff && isStaffAttendanceRoute && isStaffParentBreadcrumb) {
                    allowNavigation = false;
                }

                breadcrumbs.push({
                    label: profileName || route.data['breadcrumb'],
                    url: allowNavigation ? breadcrumbUrl : undefined,
                });
            }
        } else if (breadcrumb == 'Staff-Profile') {
            const label = `${route.params['id'] ? (await this.getName(breadcrumb, route.params['id'])) : ''}`
            const breadcrumbUrl = '/' + routeUrl.join('/');
            const hasPermission = this.hasPermissionForUrl(breadcrumbUrl);

            // For staff users in attendance/profile routes, disable parent breadcrumbs
            let allowNavigation = isAdmin || (isStaff && hasPermission) || (isParentOrGuardian && hasPermission);

            if (isStaff && isStaffAttendanceRoute && isStaffParentBreadcrumb) {
                // Staff users cannot navigate to staff parent breadcrumbs in attendance routes
                allowNavigation = false;
            }

            breadcrumbs.push({
                label: label,
                url: allowNavigation ? breadcrumbUrl : undefined,
            });
        }
        else if (breadcrumb == 'Draft Letter') {
            const label = `${route.data['breadcrumb']} ${route.params['id'] ? ' / ' + (await this.getName(breadcrumb, route.params['id'])) : ''}`;
            const breadcrumbUrl = '/' + routeUrl.join('/');
            const hasPermission = this.hasPermissionForUrl(breadcrumbUrl);

            breadcrumbs.push({
                label: label,
                url: (isAdmin || (isStaff && hasPermission) || (isParentOrGuardian && hasPermission)) ? breadcrumbUrl : undefined,
            });
        }
        else if (breadcrumb == 'Program') {
            const baseRoute = 'myschool/programs/programProfile';
            const idSegment = route.params['id'] ? `/${route.params['id']}` : '';
            const programDetailsSegment = '/programDetails';

            const label = `${route.data['breadcrumb']} ${route.params['id'] ? ' / ' + (await this.getName(breadcrumb, route.params['id'])) : ''}`;
            const generatedUrl = `/${baseRoute}${idSegment}${programDetailsSegment}`;
            const hasPermission = this.hasPermissionForUrl(generatedUrl);

            breadcrumbs.push({
                label: label,
                url: (isAdmin || (isStaff && hasPermission) || (isParentOrGuardian && hasPermission)) ? generatedUrl : undefined,
            });
        }
        else if (breadcrumb == 'Subsidy Profile') {
            const label = `${route.params['id'] ? (await this.getName(breadcrumb, route.params['id'])) : ''}`
            const breadcrumbUrl = '/' + routeUrl.join('/');
            const hasPermission = this.hasPermissionForUrl(breadcrumbUrl);

            breadcrumbs.push({
                label: label,
                url: (isAdmin || (isStaff && hasPermission) || (isParentOrGuardian && hasPermission)) ? breadcrumbUrl : undefined,
            });
        }
        else if (breadcrumb == 'Ledger Transaction') {
            const label = `${route.params['id'] ? (await this.getName(breadcrumb, route.params['id'])) : ''}`
            const breadcrumbUrl = '/' + routeUrl.join('/');
            const hasPermission = this.hasPermissionForUrl(breadcrumbUrl);

            breadcrumbs.push({
                label: label,
                url: (isAdmin || (isStaff && hasPermission) || (isParentOrGuardian && hasPermission)) ? breadcrumbUrl : undefined,
            });
        }
        else {
            if (parentBreadcrumb != undefined) {
                const label = `${route.data['breadcrumb']} ${route.params['id'] ? ' ' + (await this.getName(parentBreadcrumb, route.params['id'])) : ''}`;
                const breadcrumbUrl = '/' + routeUrl.join('/');
                const hasPermission = this.hasPermissionForUrl(breadcrumbUrl);

                // For staff users in attendance/profile routes, disable parent breadcrumbs
                let allowNavigation = isAdmin || (isStaff && hasPermission) || (isParentOrGuardian && hasPermission);

                if (isStaff && isStaffAttendanceRoute && isStaffParentBreadcrumb) {
                    // Staff users cannot navigate to staff parent breadcrumbs in attendance routes
                    allowNavigation = false;
                }

                breadcrumbs.push({
                    label: label,
                    url: allowNavigation ? breadcrumbUrl : undefined,
                });
            } else {
                const label = `${route.data['breadcrumb']}`;
                const breadcrumbUrl = '/' + routeUrl.join('/');
                const hasPermission = this.hasPermissionForUrl(breadcrumbUrl);

                // For staff users in attendance/profile routes, disable parent breadcrumbs
                let allowNavigation = isAdmin || (isStaff && hasPermission) || (isParentOrGuardian && hasPermission);

                if (isStaff && isStaffAttendanceRoute && isStaffParentBreadcrumb) {
                    // Staff users cannot navigate to staff parent breadcrumbs in attendance routes
                    allowNavigation = false;
                }

                breadcrumbs.push({
                    label: label,
                    url: allowNavigation ? breadcrumbUrl : undefined,
                });
            }
        }
    }

    if (route.firstChild) {
        await this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
    }
}

    async getName(name: any, id: number) {
        let profileName;        
        if ((name === 'Overview' || name === 'Shift Profile' || name === 'Classroom') && id !== undefined) {
            try {
                const req = await this.lookupService
                    .getRoomById(id)
                    .toPromise();
                profileName = req?.body?.name;
            } catch (error) {
                console.error('Error occurred while fetching room by ID:');
            }
        } else if ((name === 'Students' || name === 'Student Activity Profile') && id !== undefined) {
            try {
                const req = await this.lookupService
                    .getStudentById(id)
                    .toPromise();
                profileName = req?.body?.fullName;
            } catch (error) {
                console.error('Error occurred while fetching student by ID:');
            }
        } else if (name === 'Program' && id !== undefined) {
            try {
                const req = await this.lookupService
                    .getProgramById(id)
                    .toPromise();
                profileName = req?.body?.name;
            } catch (error) {
                console.error('Error occurred while fetching program by ID:');
            }
        } else if ((name == 'Staff-Profile' || name === 'Schedule Profile') && id != undefined) {
            try {
                const req = await this.lookupService
                    .getStaffById(id)
                    .toPromise();
                profileName = req?.body?.name;
            } catch (error) {
                console.error('Error occurred while fetching staff by ID:');
            }
        } else if (name == 'Staff-Profile' && id != undefined) {
            try {
                const req = await this.lookupService
                    .getStaffById(id)
                    .toPromise();
                profileName = req?.body?.name;
            } catch (error) {
                console.error('Error occurred while fetching staff by ID:');
            }
        }
        else if (name == 'Staff Attendance Profile' && id != undefined) {
            try {
                const req = await this.lookupService
                    .getStaffById(id)
                    .toPromise();
                profileName = req?.body?.name;
            } catch (error) {
                console.error('Error occurred while fetching staff by ID:');
            }
        }
        else if (name == `Student's Attendance` && id != undefined) {
            try {
                const req = await this.lookupService
                    .getStudentById(id)
                    .toPromise();
                profileName = req?.body?.fullName;
            } catch (error) {
                console.error('Error occurred while fetching staff by ID:');
            }
        }
        // else if (name == 'Family Transaction' && id != undefined) {
        //     try {
        //         const req = await this.lookupService
        //             .getStudentById(id)
        //             .toPromise();
        //         let fullName = req?.body?.fullName ?? "";
        //         let nameArray = fullName.split(" ");
        //         profileName = nameArray.length > 1 ? nameArray[nameArray.length - 1] : '';
        //     } catch (error) {
        //         console.error('Error occurred while fetching student by ID:');
        //     }
        // }
        else if (name == 'State Subsidy' && id != undefined) {
            try {
                const req = await this.lookupService
                    .getStudentById(id)
                    .toPromise();
                let fullName = req?.body?.fullName ?? "";
                let nameArray = fullName.split(" ");
                profileName = nameArray.length > 1 ? nameArray[nameArray.length - 1] : '';
            } catch (error) {
                console.error('Error occurred while fetching student by ID:');
            }
        }
        else if (name == 'Subsidy Profile' && id != undefined) {
            try {
                const req = await this.lookupService
                    .getLedgerAccountById(id)
                    .toPromise();
                let name = req?.body?.name ?? "";
                // let nameArray = name.split(" ");
                profileName = name.length > 1 ? name : '';
            } catch (error) {
                console.error('Error occurred while fetching student by ID:');
            }
        }
        else if (name == 'Ledger Transaction' && id != undefined) {
            try {
                const req = await this.lookupService
                    .getLedgerAccountById(id)
                    .toPromise();
                let name = req?.body?.name ?? "";
                // let nameArray = name.split(" ");
                profileName = name.length > 1 ? name : '';
            } catch (error) {
                console.error('Error occurred while fetching student by ID:');
            }
        }

        return profileName;
    }
}
